import Vue from 'vue';
import Router from 'vue-router';
import { requireAuth, redirectToUserHome } from '../utils/auth';
import _ from 'lodash';
import store from '../store';

const Accounts = () => import('../components/Platform/Accounts/Accounts');
const Adverts = () => import('../components/Platform/Adverts/Adverts');
const BankImports = () => import('../components/Platform/BankImports/BankImports');
const Banks = () => import('../components/Platform/Banks/Banks');
const Bills = () => import('../components/Platform/Bills/Bills');
const Brokers = () => import('../components/Platform/Brokers/Brokers');
const Customer = () => import('../components/Platform/Customers/Customer');
const CustomerPage = () => import('../components/Platform/Customers/CustomerPage');
const Customers = () => import('../components/Platform/Customers/Customers');
const Dashboards = () => import('../components/Platform/Dashboards/Dashboards');
const Dialler = () => import('../components/Platform/Dialler/Dialler');
const ForgottenPassword = () => import('../components/Authorisation/ForgottenPassword');
const Funders = () => import('../components/Platform/Funders/Funders');
const Login = () => import('../components/Authorisation/Login');
const Maintenance = () => import('../components/Maintenance/Maintenance');
const NotFound404 = () => import('../components/Platform/NotFound404');
const Partners = () => import('../components/Platform/Partners/Partners');
const Platform = () => import('../components/Platform');
const Platforms = () => import('../components/Platform/Platforms/Platforms');
const ProductSuitability = () => import('../components/Platform/ProductSuitability/ProductSuitability');
const ProductTypes = () => import('../components/Platform/ProductTypes/ProductTypes');
const Questions = () => import('../components/Platform/Questions/Questions');
const Referral = () => import('../components/Platform/Referrals/ReferralView');
const Referrals = () => import('../components/Platform/Referrals/Referrals');
const ResetPassword = () => import('../components/Authorisation/ResetPassword');
const User = () => import('../components/Platform/User/User');

Vue.use(Router);

let routes = [
  {
    path: '*',
    name: 'Maintenance',
    component: Maintenance
  }
];

if (process.env.VUE_APP_MAINTENANCE_MODE !== 'true') {
  routes = [
    {
      path: '/login',
      name: 'Login',
      component: Login
    },
    {
      path: '/forgotten-password',
      name: 'ForgottenPassword',
      component: ForgottenPassword
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword
    },
    {
      path: '/',
      name: 'Platform',
      component: Platform,
      beforeEnter: requireAuth,
      children: [
        {
          path: '/accounts',
          name: 'Accounts',
          component: Accounts,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/adverts',
          name: 'Adverts',
          component: Adverts,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/banks',
          name: 'Banks',
          component: Banks,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin', 'Bank'] }
        },
        {
          path: '/bills',
          name: 'Bills',
          component: Bills,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Accounts', 'Platform', 'Super Admin'] }
        },
        {
          path: '/customers',
          name: 'Customers',
          component: Customers,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Call Centre', 'Broker', 'Platform', 'Property Broker', 'Bank', 'Provider', 'Owner', 'Introducer', 'Partner', 'Super Admin']  }
        },
        {
          path: '/customers/:id',
          name: 'Customer',
          component: CustomerPage,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Call Centre', 'Broker', 'Platform', 'Property Broker', 'Bank', 'Provider', 'Owner', 'Introducer', 'Partner', 'Super Admin']  },
          children: [
            {
              path: ':enquiryId',
              name: 'CustomerEnquiry',
              component: Customer,
              beforeEnter: requireAuth,
              meta: { requiresRole: ['Call Centre', 'Broker', 'Platform', 'Property Broker', 'Bank', 'Provider', 'Owner', 'Introducer', 'Partner', 'Super Admin'] },
            }
          ]
        },
        {
          path: '/dashboard',
          name: 'Dashboard',
          component: Dashboards,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin', 'Partner', 'Provider'] }
        },
        {
          path: '/dialler',
          name: 'Dialler',
          component: Dialler,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Call Centre', 'Platform', 'Owner', 'Super Admin', 'Partner', 'Provider'] },
          props: true
        },
        {
          path: '/dialler/:diallerQueueId?/:phoneCallId?',
          name: 'Dialler',
          component: Dialler,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Call Centre', 'Platform', 'Owner', 'Super Admin', 'Partner', 'Provider'] },
          props: true
        },
        {
          path: '/funders',
          name: 'Funders',
          component: Funders,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin', 'Provider'] }
        },
        {
          path: '/imports',
          name: 'Imports',
          component: BankImports,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/partners',
          name: 'Partners',
          component: Partners,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin', 'Partner'] }
        },
        {
          path: '/referrals',
          name: 'Referrals',
          component: Referrals,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Call Centre', 'Broker', 'Platform', 'Property Broker', 'Bank', 'Provider', 'Owner', 'Introducer', 'Partner', 'Super Admin'] },
        },
        {
          path: '/referrals/:id',
          name: 'Referral',
          component: Referral,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Call Centre', 'Broker', 'Platform', 'Property Broker', 'Bank', 'Provider', 'Owner', 'Introducer', 'Partner', 'Super Admin'] },
        },
        {
          path: '/questions',
          name: 'Questions',
          component: Questions,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/platforms',
          name: 'Platforms',
          component: Platforms,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/brokers',
          name: 'Brokers',
          component: Brokers,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin', 'Broker'] }
        },
        {
          path: '/product-suitability',
          name: 'ProductSuitability',
          component: ProductSuitability,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/product-types',
          name: 'ProductTypes',
          component: ProductTypes,
          beforeEnter: requireAuth,
          meta: { requiresRole: ['Platform', 'Super Admin'] }
        },
        {
          path: '/me',
          name: 'User',
          component: User,
          beforeEnter: requireAuth
        },
        {
          path: '*',
          name: 'NotFound404',
          component: NotFound404,
          beforeEnter: requireAuth
        }
      ]
    }
  ];
}

const router = new Router({
  mode: 'history',
  routes
});

router.beforeEach((to, from, next) => {
  if (_.has(to.query, 'ref')) {
    const id = to.query.ref;
    store.dispatch('setReferralId', { id });
  }

  next();
});

router.beforeEach(redirectToUserHome);

export default router;
